import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";

@Component({
    tag: "ux-radio-button",
    styleUrl: "radio-button.scss",
    shadow: false
})
export class RadioButton {
    private radioButtonElement: HTMLLabelElement | null = null;
    private radioInputElement: HTMLInputElement;
    private labelElement: HTMLSpanElement | null = null;
    private descriptionToggleButton: HTMLButtonElement | null = null;
    // @ts-expect-error Check for typing
    private descriptionModal: HTMLUxModalElement = null;

    @Element() element!: HTMLUxRadioButtonElement;

    /**
     * Value of the input element, not visible to the user
     */
    @Prop() readonly value: string = "";

    /**
     * Text inside the radio button label element, visible to the user
     */
    @Prop() readonly label: string = "";

    /**
     * Input id attribute
     */
    /* eslint-disable-next-line */
    @Prop() inputId?: string;

    /**
     * Name which is used for radio button groups or as an identifier in forms
     */
    @Prop() readonly name: string = "";

    /**
     * Indicates if the radio button is checked
     */
    @Prop() readonly checked: boolean = false;

    /**
     * Indicates if the radio button is disabled
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * Indicates if the radio button is required
     */
    @Prop() readonly required: boolean = false;

    /**
     * Indicates if the radio button has an error state
     */
    @Prop() readonly error: boolean = false;

    /**
     * Error message that is rendered if the component has an error
     */
    @Prop() readonly errorMsg: string | null = null;

    /**
     * If set, a green border will be applied to the radio button area when selected
     */
    @Prop() readonly withBorder: boolean = false;

    /**
     * If set, the checkbox will be displayed as a box with shadow
     */
    @Prop() readonly asBox: boolean = false;

    @State() hasMedia = false;
    @State() hasDescription = false;
    @State() isDescriptionActive = false;
    @State() borderActive = false;
    @State() groupName: string | null = null;

    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }

    componentWillLoad() {
        this.borderActive = this.withBorder && this.checked;
        this.setInputId();
        this.hasMedia = !!this.element.querySelector('[slot="media"]');
        this.hasDescription = !!this.element.querySelector('[slot="description"]');
    }

    componentDidLoad() {
        this.checkForButtonGroup();
    }

    private checkForButtonGroup = () => {
        /** check if outer radio button group exists*/
        // @ts-expect-error Check for typing
        if (this.radioButtonElement.closest(".ux-radio-button-group") === null) {
            return;
        }
        // @ts-expect-error Check for typing
        this.groupName = this.radioButtonElement.closest(".ux-radio-button-group").getAttribute("name");
        return this.groupName;
    };

    private handleComponentClick = (e: Event) => {
        const eTarget = e.target as HTMLElement;
        if (
            eTarget !== this.radioButtonElement &&
            eTarget !== this.radioInputElement &&
            eTarget !== this.descriptionToggleButton &&
            !(this.descriptionModal !== null && eTarget.closest(`ux-modal`) === this.descriptionModal)
        ) {
            if (!this.radioInputElement.checked) {
                this.radioInputElement.checked = true;
                this.onRadioButtonChange();
            }
        }
    };

    /**
     * Radio button event, will be emitted when the radio button becomes active
     */
    @Event({
        eventName: "uxRadioButtonChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    radioButtonEvent!: EventEmitter<{
        name: string;
        value: string;
        inputId: string;
        checked: boolean;
    }>;

    private onRadioButtonChange = () => {
        this.radioButtonEvent.emit({
            name: this.name,
            value: this.value,
            // @ts-expect-error Check for typing
            inputId: this.inputId,
            checked: this.radioInputElement?.checked
        });
    };

    @Listen("uxRadioButtonChange", { target: "window" })
    handleElementBorder(e: CustomEvent) {
        if (!this.withBorder) {
            return;
        }
        if (e.target === this.element) {
            this.borderActive = true;
        } else if (this.name === e.detail.name) {
            this.borderActive = false;
        }
    }

    /**
     * Returns the innerHTML of the label element
     */
    @Method()
    async getLabel() {
        // @ts-expect-error Check for typing
        return this.labelElement.innerHTML;
    }

    private handleDescription = (e: Event) => {
        e.stopImmediatePropagation();
        this.isDescriptionActive = !this.isDescriptionActive;
    };

    render() {
        /** base class is ux-radio-button-field because ux-radio-button is already used by the radio-button static component */
        const baseClass = "ux-radio-button-field";

        const { value, label, inputId, name, checked, disabled, required, error, errorMsg } = this;

        const radioName = this.groupName !== null ? this.checkForButtonGroup() : name;
        const handleBoxClick = this.hasMedia || this.withBorder || this.asBox || this.hasDescription;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--with-media`]: this.hasMedia,
                    [`${baseClass}--active`]: this.checked,
                    [`${baseClass}--as-box`]: (this.hasDescription && !this.hasMedia) || this.asBox
                }}
                onClick={handleBoxClick ? this.handleComponentClick : null}
            >
                <div style={{ display: this.hasMedia ? "block" : "none" }} class={`${baseClass}__media-container`}>
                    <slot name="media" />
                </div>
                <div class={`${baseClass}__label-container`}>
                    {this.borderActive && <div class={`${baseClass}__border`}></div>}
                    <label
                        class={{
                            [`ux-radio-button`]: true,
                            [`ux-radio-button--disabled`]: disabled,
                            [`ux-radio-button--error`]: error
                        }}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.radioButtonElement = el)}
                    >
                        <input
                            class="ux-radio-button__input"
                            type="radio"
                            id={inputId}
                            // @ts-expect-error Check for typing
                            name={radioName}
                            value={value}
                            checked={checked}
                            disabled={disabled}
                            required={required}
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.radioInputElement = el)}
                            onChange={this.onRadioButtonChange}
                            aria-invalid={error ? "true" : "false"}
                        />
                        {/* @ts-expect-error Check for typing */}
                        <span class="ux-radio-button__label" ref={(el) => (this.labelElement = el)}>
                            {label}
                            <slot />
                            {required && <span> *</span>}{" "}
                            {error && errorMsg !== "" && <span class="ux-label__error-message">{errorMsg}</span>}
                        </span>
                    </label>
                    {this.hasDescription && (
                        <button
                            class={`${baseClass}__description-toggle`}
                            // @ts-expect-error Check for typing
                            onClick={this.hasDescription ? this.handleDescription : null}
                            type="button"
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.descriptionToggleButton = el)}
                        >
                            <ux-icon name="square-info" weight="bold" size-s="15"></ux-icon>
                        </button>
                    )}
                </div>
                {this.hasDescription && (
                    <ux-modal
                        opened={this.isDescriptionActive}
                        name={"description-modal" + this.inputId}
                        onUxModalClose={() => (this.isDescriptionActive = false)}
                        no-gutter-top={true}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.descriptionModal = el)}
                        closeOnEscKey
                        closeOnBackdropClick
                    >
                        <slot name="description" />
                    </ux-modal>
                )}
            </Host>
        );
    }
}
